/**
 * Traduce los errores crudos que devuelve la API a mensajes entendibles por el
 * usuario. El caso principal son los errores de clave duplicada de Mongo, que
 * gas-datos reenvía como `{ campo: "valor" } ya existe` (o, si algún path los
 * filtra sin procesar, como el string crudo `E11000 duplicate key ...`).
 *
 * Si el mensaje no matchea ningún patrón conocido se devuelve tal cual, así que
 * es seguro usarlo en cualquier error.
 */

// Nombres técnicos de campo -> etiqueta amigable en español.
const ETIQUETAS_CAMPO: Record<string, string> = {
  deveui: 'DevEUI',
  deviceName: 'nombre de dispositivo',
  numeroSerie: 'número de serie',
  numeroSerieCorrectora: 'número de serie de correctora',
  telefono: 'teléfono',
  email: 'email',
  mail: 'email',
  username: 'nombre de usuario',
  usuario: 'nombre de usuario',
  nombre: 'nombre',
  codigoSimec: 'código SIMEC',
  dni: 'DNI',
  cuit: 'CUIT',
  patente: 'patente',
  idCliente: 'cliente',
  timestamp: 'fecha y hora',
};

// Campos internos que no aportan valor al usuario final; se ocultan si el
// índice duplicado también incluye algún campo relevante.
const CAMPOS_INTERNOS = new Set([
  'idCliente',
  'idUnidadNegocio',
  'idCentroOperativo',
  'idLocalidad',
]);

function etiquetaCampo(campo: string): string {
  return ETIQUETAS_CAMPO[campo] ?? campo;
}

// Limpia el valor: quita comillas envolventes y wrappers tipo ObjectId('...') o
// ISODate('...') que aparecen en el `dup key` de Mongo.
function limpiarValor(valorCrudo: string): string {
  const valor = valorCrudo.trim();
  const wrapper = valor.match(/^[A-Za-z]+\(['"]?([^'")]+)['"]?\)$/);
  if (wrapper) {
    return wrapper[1];
  }
  return valor.replace(/^["']|["']$/g, '');
}

// Parsea el contenido de un objeto Mongo `campo: valor, campo2: valor2`.
function parsearCampos(contenido: string): { campo: string; valor: string }[] {
  return contenido
    .split(',')
    .map((par) => {
      const idx = par.indexOf(':');
      if (idx === -1) {
        return null;
      }
      const campo = par
        .slice(0, idx)
        .trim()
        .replace(/^["']|["']$/g, '');
      const valor = limpiarValor(par.slice(idx + 1));
      return { campo, valor };
    })
    .filter((x): x is { campo: string; valor: string } => !!x && !!x.campo);
}

function construirMensajeDuplicado(contenido: string): string {
  let campos = parsearCampos(contenido);
  if (!campos.length) {
    return 'Ya existe un registro con esos datos.';
  }
  const relevantes = campos.filter((c) => !CAMPOS_INTERNOS.has(c.campo));
  if (relevantes.length) {
    campos = relevantes;
  }
  const partes = campos.map((c) => `${etiquetaCampo(c.campo)} "${c.valor}"`);
  const lista =
    partes.length === 1
      ? partes[0]
      : `${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1]}`;
  return `Ya existe un registro con ${lista}.`;
}

// Extrae el string de mensaje del error respetando la precedencia histórica
// (mensaje localizado > message de NestJS), y colapsa arrays (class-validator).
function extraerMensajeCrudo(error: any): string | undefined {
  if (typeof error === 'string') {
    return error;
  }
  const mensaje =
    error?.error?.mensaje ||
    error?.mensaje ||
    error?.error?.message ||
    error?.message;
  if (Array.isArray(mensaje)) {
    return mensaje.filter(Boolean).join('. ');
  }
  return typeof mensaje === 'string' ? mensaje : undefined;
}

export function parsearMensajeError(error: any): string {
  const mensaje = extraerMensajeCrudo(error);
  if (!mensaje || !mensaje.trim()) {
    return 'Error desconocido, ver logs';
  }

  // Duplicado ya traducido por gas-datos: `{ campo: "valor" } ya existe`.
  const yaExiste = mensaje.match(/\{([^{}]*)\}\s*ya existe/i);
  if (yaExiste) {
    return construirMensajeDuplicado(yaExiste[1]);
  }

  // Error crudo de Mongo (E11000) por si algún path lo filtra sin procesar.
  if (/E11000|duplicate key/i.test(mensaje)) {
    const dup = mensaje.match(/dup key:\s*\{([^{}]*)\}/i);
    if (dup) {
      return construirMensajeDuplicado(dup[1]);
    }
    return 'Ya existe un registro con esos datos.';
  }

  return mensaje;
}
